import { Controller } from "@hotwired/stimulus"

// Overlay sur l'iframe de démo : transmet hover + scroll (wheel et swipe touch).
// Le clic navigue vers login_path (link_to natif, cf. home/index.html.erb) —
// on bloque juste le mousedown pour éviter la sélection de texte pendant le forward.
export default class extends Controller {
  static targets = ["iframe", "overlay"]
  static values = { scale: { type: Number, default: 0.885 } }

  connect() {
    this._onMouseMove  = this.#forwardMouse.bind(this, "mousemove")
    this._onMouseLeave = this.#forwardMouse.bind(this, "mouseleave")
    this._onWheel      = this.#forwardWheel.bind(this)
    this._onTouchStart = this.#onTouchStart.bind(this)
    this._onTouchMove  = this.#onTouchMove.bind(this)
    this._onTouchEnd   = this.#onTouchEnd.bind(this)

    this.overlayTarget.addEventListener("mousemove",  this._onMouseMove)
    this.overlayTarget.addEventListener("mouseleave", this._onMouseLeave)
    this.overlayTarget.addEventListener("wheel",      this._onWheel,      { passive: false })
    this.overlayTarget.addEventListener("touchstart", this._onTouchStart, { passive: true })
    this.overlayTarget.addEventListener("touchmove",  this._onTouchMove,  { passive: false })
    this.overlayTarget.addEventListener("touchend",   this._onTouchEnd,   { passive: true })
  }

  disconnect() {
    this.overlayTarget.removeEventListener("mousemove",  this._onMouseMove)
    this.overlayTarget.removeEventListener("mouseleave", this._onMouseLeave)
    this.overlayTarget.removeEventListener("wheel",      this._onWheel)
    this.overlayTarget.removeEventListener("touchstart", this._onTouchStart)
    this.overlayTarget.removeEventListener("touchmove",  this._onTouchMove)
    this.overlayTarget.removeEventListener("touchend",   this._onTouchEnd)
    this.#cancelMomentum()
  }

  blockMousedown(event) {
    event.preventDefault()
  }

  // --- private ---

  #forwardMouse(type, event) {
    const doc = this.iframeTarget.contentDocument
    if (!doc) return

    const { x, y } = this.#iframeCoords(event)
    const el = doc.elementFromPoint(x, y)
    if (!el) return

    el.dispatchEvent(new MouseEvent(type, {
      bubbles: true, cancelable: true,
      view: this.iframeTarget.contentWindow,
      clientX: x, clientY: y,
    }))
  }

  #forwardWheel(event) {
    const win = this.iframeTarget.contentWindow
    if (!win) return

    if (this.#atBoundary(win, event.deltaY)) return // laisse la page parente scroller

    event.preventDefault()
    win.scrollBy({ top: event.deltaY / this.scaleValue, behavior: "auto" })
  }

  #onTouchStart(event) {
    this.#cancelMomentum()
    // Annule le momentum scroll de la page si elle était encore en inertie
    window.scrollTo(window.scrollX, window.scrollY)
    const touch = event.touches[0]
    this._lastTouchY    = touch.clientY
    this._touchHistory  = [{ y: touch.clientY, t: performance.now() }]
  }

  #onTouchMove(event) {
    const win = this.iframeTarget.contentWindow
    if (!win) return

    const touch    = event.touches[0]
    const currentY = touch.clientY
    const deltaY   = this._lastTouchY - currentY
    this._lastTouchY = currentY

    // Mise à jour de l'historique pour le calcul de vélocité (fenêtre de 100ms)
    const now = performance.now()
    this._touchHistory.push({ y: currentY, t: now })
    const cutoff = now - 100
    this._touchHistory = this._touchHistory.filter(p => p.t >= cutoff)

    // Si le doigt a quitté la zone de l'overlay, on laisse le browser gérer
    const rect = this.overlayTarget.getBoundingClientRect()
    const insideOverlay = touch.clientX >= rect.left && touch.clientX <= rect.right &&
                          touch.clientY >= rect.top  && touch.clientY <= rect.bottom
    if (!insideOverlay) return

    if (this.#atBoundary(win, deltaY)) return // laisse la page parente gérer le swipe

    event.preventDefault()
    win.scrollBy({ top: deltaY / this.scaleValue, behavior: "auto" })
  }

  #onTouchEnd(_event) {
    const win = this.iframeTarget.contentWindow
    if (!win || !this._touchHistory || this._touchHistory.length < 2) return

    const first = this._touchHistory[0]
    const last  = this._touchHistory[this._touchHistory.length - 1]
    const dt    = last.t - first.t
    if (dt <= 0) return

    // vélocité en px/ms, positive = scroll vers le bas
    const velocityPxMs = (first.y - last.y) / dt
    if (Math.abs(velocityPxMs) < 0.2) return

    this.#startMomentum(win, velocityPxMs)
  }

  #startMomentum(win, velocityPxMs) {
    const FRICTION = 0.97
    const FRAME_MS = 1000 / 60
    let   velocity = velocityPxMs * FRAME_MS // px/frame à 60fps
    let   lastTime = performance.now()

    const step = (now) => {
      const elapsed = now - lastTime
      lastTime = now
      velocity *= Math.pow(FRICTION, elapsed / FRAME_MS)

      if (Math.abs(velocity) < 0.5 || this.#atBoundary(win, velocity)) return

      win.scrollBy({ top: velocity / this.scaleValue, behavior: "auto" })
      this._momentumRaf = requestAnimationFrame(step)
    }

    this._momentumRaf = requestAnimationFrame(step)
  }

  #cancelMomentum() {
    if (this._momentumRaf) {
      cancelAnimationFrame(this._momentumRaf)
      this._momentumRaf = null
    }
  }

  // Retourne true si on est en bout de scroll dans la direction demandée.
  #atBoundary(win, delta) {
    const el       = win.document.documentElement
    const atTop    = el.scrollTop <= 0
    const atBottom = el.scrollTop >= el.scrollHeight - el.clientHeight - 1
    return (delta < 0 && atTop) || (delta > 0 && atBottom)
  }

  #iframeCoords(event) {
    const rect  = this.iframeTarget.getBoundingClientRect()
    const scale = this.scaleValue
    return {
      x: (event.clientX - rect.left) / scale,
      y: (event.clientY - rect.top)  / scale,
    }
  }
}
